/**
 * Flota → procese-verbale: tipurile raspunsurilor si bucatile comune paginilor (task CRM #2445).
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../../../lib/api'
import { Pastila } from '../../../components/ui/Pastila'

export type StatusPv = 'ciorna' | 'semnat' | 'anulat'

export interface PvRand {
  id: number
  numar: string | null
  vehicleId: number
  plate: string
  predatLa: string
  predatorId: number | null
  primitorId: number | null
  numePredator: string
  numePrimitor: string
  kilometraj: number
  status: StatusPv
  areScan: boolean
  numeCreator: string | null
  createdAt: string
  retroactiv: boolean
}

export interface Problema {
  cod: string
  mesaj: string
  pvId?: number
}

export interface PvDetaliu {
  id: number
  numar: string | null
  vehicleId: number
  vehicle: { id: number; plate: string; brand: string | null; model: string | null }
  predatorId: number | null
  primitorId: number | null
  predatLa: string
  kilometraj: number
  nivelCarburant: number
  stare: string | null
  daune: string | null
  actTalon: boolean
  actRca: boolean
  actItp: boolean
  actRovinieta: boolean
  cardCarburant: boolean
  nrChei: number
  alteAccesorii: string | null
  observatii: string | null
  status: StatusPv
  areScan: boolean
  scanNume: string | null
  scanIncarcatLa: string | null
  semnatLa: string | null
  anulatLa: string | null
  motivAnulare: string | null
  createdAt: string
  numePredator: string
  numePrimitor: string
  numeCreator: string | null
  numeSemnatar: string | null
  numeAnulator: string | null
  retroactiv: boolean
  poze: { id: number; numeFisier: string; descriere: string | null; contentType: string }[]
  verificare: { erori: Problema[]; avertizari: Problema[] } | null
}

export interface Om {
  id: number
  name: string
  jobTitle: string | null
}

export interface MasinaScurt {
  id: number
  plate: string
  brand: string | null
  model: string | null
  active: boolean
  driverName: string | null
}

export interface Consum {
  cost: number
  litri: number
  nrTx: number
}

export const ron = (v: number) =>
  new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(v)

/** Data si ora, in ora Romaniei. */
export const cand = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleString('ro-RO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'

export const ziua = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('ro-RO') : '—'

export function StarePv({ status }: { status: StatusPv }) {
  if (status === 'semnat') return <Pastila ton="verde">semnat</Pastila>
  if (status === 'anulat') return <Pastila ton="gri">anulat</Pastila>
  return <Pastila ton="galben">ciornă</Pastila>
}

export function useOameni() {
  return useQuery({
    queryKey: ['flota', 'oameni'],
    queryFn: async () => (await api.get<{ rows: Om[] }>('/flota/oameni')).data.rows,
    staleTime: 10 * 60 * 1000,
  })
}

export function useMasini() {
  return useQuery({
    queryKey: ['flota', 'masini'],
    queryFn: async () => (await api.get<{ rows: MasinaScurt[] }>('/flota/masini')).data.rows,
    staleTime: 5 * 60 * 1000,
  })
}

/** Linkul spre PV-ul de completat, cu masina si data puse. */
export function linkPvNou(o: { vehicleId?: number; la?: string; predatorId?: number | null }) {
  const p = new URLSearchParams()
  if (o.vehicleId) p.set('masina', String(o.vehicleId))
  if (o.la) p.set('la', o.la)
  if (o.predatorId) p.set('predator', String(o.predatorId))
  const q = p.toString()
  return `/operational/flota/pv/nou${q ? `?${q}` : ''}`
}

/** Numele omului, cu link spre pagina lui; firma ramane text. */
export function LinkSofer({ id, nume }: { id: number | null; nume: string }) {
  if (id == null) return <span className="text-slate-500">{nume}</span>
  return (
    <Link to={`/operational/flota/soferi/${id}`} className="text-orange-700 hover:underline">
      {nume}
    </Link>
  )
}

export function mesajEroare(err: unknown): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ??
    (err as Error)?.message ??
    'A apărut o eroare.'
  )
}
